import { useMemo } from "react"

import {
  DELETE_PROJECT_OVERLAY,
  DUPLICATE_PROJECT_OVERLAY,
  SHARE_OVERLAY
} from "shared/context/overlay/types"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"
import { Project } from "shared/types/Project"

import { useProjectChildrenSorting } from "entities/project-children/api/useProjectChildrenSorting"
import { useDeleteProject } from "entities/projects/api/useDeleteProject"
import { useProjectData } from "entities/projects/api/useProjectData"
import { useUpdateProject } from "entities/projects/api/useUpdateProject"
import { getProductTotal } from "entities/projects/lib/getProductTotal"
import { useProjectPermissions } from "entities/projects/model/useProjectPermissions"
import { useProjectState } from "entities/projects/model/useProjectState"
import { useProjectStore } from "features/project-management/model/useProjectStore"

import { useProjectChildrenCache } from "./useProjectChildrenCache"

export function useProject(id: string, cacheProducts?: boolean) {
  const { openOverlay: openShareOverlay } = useOverlay(SHARE_OVERLAY)
  const { openOverlay: openDeleteOverlay } = useOverlay(DELETE_PROJECT_OVERLAY)
  const { openOverlay: openDuplicateOverlay } = useOverlay(
    DUPLICATE_PROJECT_OVERLAY
  )

  const { project, isLoading, error } = useProjectData(id, true)
  const { permissions, isLoading: isLoadingPermissions } =
    useProjectPermissions({
      projectID: id
    })
  const { isPending, isUpdatingChildren } = useProjectState(id)
  const { activeFloorID } = useProjectStore(id)

  useProjectChildrenCache(project?.children, { skip: !cacheProducts })

  const sort = useProjectChildrenSorting({
    projectID: id,
    floorID: activeFloorID
  })

  const { updateProject } = useUpdateProject()
  const { deleteProject } = useDeleteProject()

  const handleUpdate = useEvent((projectUpdates: Partial<Project>) => {
    updateProject(id, projectUpdates)
  })

  const handleOpenShareOverlay = useEvent(() => {
    openShareOverlay({ projectID: id })
  })

  const handleDuplicate = useEvent(() => {
    openDuplicateOverlay({ projectID: id })
  })

  const handleDelete = useEvent(() => deleteProject({ id }))

  const handleOpenDeleteProjectOverlay = useEvent(() => {
    openDeleteOverlay({ projectID: id })
  })

  const productTotal = useMemo(
    () => getProductTotal(project?.children),
    [project?.children]
  )

  return {
    project,
    productTotal,
    isSortingDisabled: isUpdatingChildren,
    isFavoritesProject: false,
    isLoading,
    isPending,
    isLoadingChildren: isLoading,
    permissions,
    isLoadingPermissions,
    error,
    onUpdate: handleUpdate,
    onProjectChildPositionChange: sort,
    onDelete: handleDelete,
    onOpenDeleteProjectOverlay: handleOpenDeleteProjectOverlay,
    onOpenShareOverlay: handleOpenShareOverlay,
    onDuplicate: handleDuplicate
  }
}
